import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, finalize, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import {
  ICampaignJsonDetailReport,
  ICampaignReport,
  MachineDetailedValues,
  MachinesMeasurementValue,
  MachinesOperatingValue,
  OperatingAnalysisList,
  PaginatedMachines,
} from '@cde-fe-app/app/shared/models/analytics.model';
import {
  Category,
  Characteristics,
  ComponentType,
  DataLoggerReference,
  Implement,
  ImplementType,
  Machine,
  MachineBase,
  MachineType,
} from '@cde-fe-app/app/shared/models/machine.model';
import { AnalyticsService } from '@cde-fe-app/app/shared/services/analytics.service';
import { TelemetryService } from '@cde-fe-app/app/shared/services/telemetry.service';
import * as MachinesOverviewSelectors from '@cde-fe-app/app/shared/state/machine-overview';
import { getEquipmentList } from '@cde-fe-app/app/shared/state/machine-overview';
import * as RootState from '..';
import * as AnalyticsActions from './analytics.actions';

@Injectable()
export class AnalyticsEffects {
  readonly actions$ = inject(Actions);
  readonly analyticsService = inject(AnalyticsService);
  readonly store = inject<Store<RootState.IState>>(Store<RootState.IState>);
  readonly telemetryService = inject(TelemetryService);

  measurementValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.MEASUREMENT_VALUES),
      mergeMap((data: { payload: { ids: string[]; timeZone: string }; type: string }) =>
        this.analyticsService.getMachinesMeasurementValues(data.payload.ids, data.payload.timeZone).pipe(
          take(1),
          map(
            (measurementValues: MachinesMeasurementValue[]) =>
              new AnalyticsActions.MeasurementValuesSuccess(measurementValues)
          ),
          catchError(error => of(new AnalyticsActions.MeasurementValuesFailure(error))),
          finalize(() => {
            this.store.dispatch(new AnalyticsActions.MeasurementValuesComplete());
          })
        )
      )
    )
  );

  operatingValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.OPERATING_VALUES),
      mergeMap((data: { payload: { ids: string[]; timeZone: string; widgetid?: string }; type: string }) =>
        this.analyticsService
          .getMachinesOperatingValues(data.payload.ids, data.payload.timeZone, data.payload.widgetid)
          .pipe(
            take(1),
            map(
              (operatingValues: MachinesOperatingValue[]) =>
                new AnalyticsActions.OperatingValuesSuccess(operatingValues)
            ),
            catchError(error => of(new AnalyticsActions.OperatingValuesFailure(error))),
            finalize(() => {
              this.store.dispatch(new AnalyticsActions.OperatingValuesComplete());
            })
          )
      )
    )
  );

  equipmentList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.MACHINES_LIST),
      switchMap(() => {
        const hasDataMapping = (item: MachineBase) =>
          !!item.characteristics?.includes(Characteristics.DATA_MAPPING_AVAILABLE) &&
          this.isMachineOrImplementWithUCM(item);

        return this.store.select(MachinesOverviewSelectors.getEquipmentList).pipe(
          filter((equipmentList): equipmentList is MachineBase[] => equipmentList !== null),
          map(
            equipmentList =>
              ((equipmentList ?? []).filter(hasDataMapping) as MachineBase[]).map(machine => ({
                ...machine,
                equipmentCategory:
                  machine.category === Category.IMPLEMENT
                    ? ((machine as Implement).implementCategory ?? machine.type).toLowerCase()
                    : machine.type.toLowerCase(),
                hasImplementDescriptionId: (machine.dataLoggerReferences ?? []).some(
                  (ref: DataLoggerReference) => !!ref.implementDescriptionId
                ),
              })) as Machine[]
          ),
          take(1),
          map(filteredEquipment => new AnalyticsActions.MachinesListSuccess(filteredEquipment)),
          catchError(error => of(new AnalyticsActions.MachinesListFailure(error))),
          finalize(() => {
            this.store.dispatch(new AnalyticsActions.MachinesListComplete());
          })
        );
      })
    )
  );

  isMachineOrImplementWithUCM(equipment: MachineBase): boolean {
    let isImplementWithUCM = false;
    if (equipment.category === Category.MACHINE) {
      return true;
    } else if (equipment.category === Category.IMPLEMENT) {
      for (const dataLoggerReference of equipment.dataLoggerReferences) {
        if (dataLoggerReference.equipmentId === equipment.serial) {
          isImplementWithUCM = true;
        }
      }
    }
    return isImplementWithUCM;
  }

  implementsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.IMPLEMENTS_LIST),
      switchMap(() => {
        const isImplement = (item: MachineBase) => item.category === Category.IMPLEMENT;

        return this.store.select(MachinesOverviewSelectors.getEquipmentList).pipe(
          filter((equipmentList): equipmentList is MachineBase[] => equipmentList !== null),
          map(equipmentList =>
            ((equipmentList ?? []).filter(isImplement) as Implement[]).map(impl => ({
              ...impl,
              equipmentCategory: (impl.implementCategory ?? impl.type).toLowerCase(),
              hasImplementDescriptionId: (impl.dataLoggerReferences ?? []).some(ref => !!ref.implementDescriptionId),
            }))
          ),
          take(1),
          map(list => new AnalyticsActions.ImplementsListSuccess(list)),
          catchError(error => of(new AnalyticsActions.ImplementsListFailure(error))),
          finalize(() => {
            this.store.dispatch(new AnalyticsActions.ImplementsListComplete());
          })
        );
      })
    )
  );

  machine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.MACHINE),
      map((action: AnalyticsActions.Machine) => action.payload),
      switchMap((payload: { id: string; type: MachineType | ComponentType | ImplementType }) => {
        return this.store.select(getEquipmentList).pipe(
          filter((equipmentList): equipmentList is MachineBase[] => equipmentList !== null),
          map(equipmentList => {
            const equipment = (equipmentList ?? []).find(
              (item: MachineBase) => item.id === payload.id && item.type === payload.type
            ) as MachineBase;
            return equipment;
          }),

          take(1),
          map((equipment: MachineBase) => new AnalyticsActions.MachineSuccess(equipment)),
          catchError(error => of(new AnalyticsActions.MachineFailure(error))),
          finalize(() => {
            this.store.dispatch(new AnalyticsActions.MachineComplete());
          })
        );
      })
    )
  );

  operatingAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.OPERATING_ANALYSIS),
      mergeMap(
        (data: {
          payload: {
            uuid: string;
            timeZone: string;
            measuredFromLocalDate: string;
            measuredUntilLocalDate: string;
            calculateEngineOffDuration?: boolean;
          };
        }) =>
          this.telemetryService
            .getOperatingAnalysis(
              data.payload.uuid,
              data.payload.timeZone,
              data.payload.measuredFromLocalDate,
              data.payload.measuredUntilLocalDate,
              data.payload.calculateEngineOffDuration
            )
            .pipe(
              take(1),
              map(
                (operatingAnalysis: OperatingAnalysisList[]) =>
                  new AnalyticsActions.OperatingAnalysisSuccess(operatingAnalysis)
              ),
              catchError(error => of(new AnalyticsActions.OperatingAnalysisFailure(error))),
              finalize(() => this.store.dispatch(new AnalyticsActions.OperatingAnalysisComplete()))
            )
      )
    )
  );

  machineDetailedValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.DETAILED_VALUES),
      mergeMap(
        (data: {
          payload: {
            uuid: string;
            timeZone: string;
            measuredFromLocalDate: string;
            measuredUntilLocalDate: string;
          };
        }) =>
          this.analyticsService
            .getMachineDetailedValues(
              data.payload.uuid,
              data.payload.timeZone,
              data.payload.measuredFromLocalDate,
              data.payload.measuredUntilLocalDate
            )
            .pipe(
              take(1),
              map(
                (machineDetailedValues: MachineDetailedValues) =>
                  new AnalyticsActions.DetailedValuesSuccess(machineDetailedValues)
              ),
              catchError(error => of(new AnalyticsActions.DetailedValuesFailure(error))),
              finalize(() => this.store.dispatch(new AnalyticsActions.DetailedValuesComplete()))
            )
      )
    )
  );

  paginatedMachines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.PAGINATED_MACHINES),
      mergeMap(
        (data: {
          payload: { page: number; size: number; characteristic: string; machineIds: string; archived: boolean };
        }) =>
          this.analyticsService
            .getPaginatedMachines(
              data.payload.page,
              data.payload.size,
              data.payload.characteristic,
              data.payload.machineIds,
              data.payload.archived
            )
            .pipe(
              take(1),
              map(
                (paginatedMachines: PaginatedMachines) =>
                  new AnalyticsActions.PaginatedMachinesListSuccess(paginatedMachines)
              ),
              catchError(error => of(new AnalyticsActions.PaginatedMachinesListFailure(error))),
              finalize(() => this.store.dispatch(new AnalyticsActions.PaginatedMachinesListComplete()))
            )
      )
    )
  );

  getCampaignReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.GET_CAMPAIGN_REPORT),
      map((action: AnalyticsActions.GetCampaignReport) => action.campaignReportId),
      mergeMap((campaignReportId: string) =>
        this.analyticsService.getCampaignReport(campaignReportId).pipe(
          map((campaignReport: ICampaignReport) => new AnalyticsActions.GetCampaignReportSuccess(campaignReport)),
          catchError(error => of(new AnalyticsActions.GetCampaignReportFailure(error))),
          finalize(() => {
            this.store.dispatch(new AnalyticsActions.GetCampaignReportComplete());
          })
        )
      )
    )
  );

  getJsonCampaignReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.GET_JSON_CAMPAIGN_REPORT),
      map((action: AnalyticsActions.GetJsonCampaignReport) => action.campaignReportId),
      mergeMap((campaignReportId: string) =>
        this.analyticsService.getCampaignReportJsonExport(campaignReportId).pipe(
          map(
            (campaignJsonDetailReports: ICampaignJsonDetailReport[]) =>
              new AnalyticsActions.GetJsonCampaignReportSuccess(campaignReportId, campaignJsonDetailReports)
          ),
          catchError(error => of(new AnalyticsActions.GetJsonCampaignReportFailure(error))),
          finalize(() => {
            this.store.dispatch(new AnalyticsActions.GetJsonCampaignReportComplete());
          })
        )
      )
    )
  );
}
